import { useState, useEffect, useRef, useMemo } from "react";
import { Segmented, Slider, Tooltip } from 'antd';
import {
    PlayCircleFilled,
    PauseCircleFilled,
    LoadingOutlined,
    UndoOutlined,
    FastBackwardOutlined,
    FastForwardOutlined
} from '@ant-design/icons';
import { getFormattedSeekValue } from "src/utils/helperFunctions";
import { VIDEO_SKIP } from "../../constant";

// Question types where speaking IS the answer — speech on these is expected.
// Speech on any OTHER question type is the flag.
const SPEECH_QUESTION_TYPES = ["speech_scripted", "speech_spontaneous"]

const VideoPlayer = ({
    loading,
    sessionVideoUrls,
    currentVideoIndex,
    setCurrentVideoIndex,
    isPlaying,
    setIsPlaying,
    seekValue,
    setSeekValue,
    isSessionVideoEnded,
    setIsSessionVideoEnded,
    canPlayVideoLoading,
    setCanPlayVideoLoading,
    audioEvents = []
}) => {
    const [playbackRate, setPlaybackRate] = useState(1)
    const skipVideo = useRef("")
    const videoElement = useRef()

    /**
     * VAD speech markers plotted on the timeline.
     * Each audio event carries the covering video chunk (`video[0]`) with the same
     * S3 `link` as one of this session's clips and a `seekSec` offset within it.
     * We map link -> clip index in THIS session and place the marker at
     * `index * CLIP_LENGTH + seekSec` — the player's own coordinate system — so a
     * click seeks exactly where the marker sits. (The server's global videoOffsetMs
     * spans all sessions and would drift once a second session exists.)
     * Links are compared without their query-string so pre-signed URLs still match.
     */
    const vadMarkers = useMemo(() => {
        const sliderMax = sessionVideoUrls.length * VIDEO_SKIP.CLIP_LENGTH
        if (!sliderMax || !audioEvents?.length) return []
        const baseLink = (link) => (link ? String(link).split("?")[0] : "")
        const linkToIndex = new Map(
            sessionVideoUrls.map((v, i) => [baseLink(v?.link), i])
        )
        return audioEvents.reduce((acc, event) => {
            const chunk = event?.video?.[0]
            const idx = chunk ? linkToIndex.get(baseLink(chunk.link)) : undefined
            if (idx === undefined) return acc
            const startSec = idx * VIDEO_SKIP.CLIP_LENGTH + (chunk.seekSec || 0)
            const durSec = Math.max((event.durationMs || 0) / 1000, 0)
            const questionType = event.questionType || null
            acc.push({
                startSec,
                durSec,
                leftPct: Math.min((startSec / sliderMax) * 100, 100),
                widthPct: Math.min(Math.max((durSec / sliderMax) * 100, 0.8), 100),
                questionType,
                questionIndex: event.questionIndex ?? null,
                // speech on a non-speech question (mcq/free_text/…) is suspicious;
                // on a speech question it's expected. Null type → unknown, not flagged.
                flagged: !!questionType && !SPEECH_QUESTION_TYPES.includes(questionType),
            })
            return acc
        }, [])
    }, [audioEvents, sessionVideoUrls])

    const seekToMarker = (startSec) => {
        const clipIndex = Math.floor(startSec / VIDEO_SKIP.CLIP_LENGTH)
        if (clipIndex === currentVideoIndex && videoElement.current) {
            videoElement.current.currentTime = startSec % VIDEO_SKIP.CLIP_LENGTH
            videoElement.current.play()
            setIsPlaying(true)
            setSeekValue(startSec)
        } else {
            skipVideo.current = true
            setCanPlayVideoLoading(true)
            setSeekValue(startSec)
            setCurrentVideoIndex(clipIndex)
        }
    }

    useEffect(() => {
        /**
         * @param {playbackRate} is updated of the video.
         * @param {skipVideo.current} is true if video is skipped. In that case the current time of the video needs to 
         * be updated accurately.
         * Firstly, video index is changed in @param {handleVideoSkip}
         * seekValue is the value when user presses the skip button. 
         * Number(seekValue) % 5 gives us the number of seconds starting from 0.
         * For instance, in a backward skip of 10 seconds at 0:33,
         * Number(33) % 5 will give us 3 which should be the currentTime of the video at updated index.
         * Reset @param {skipVideo} to false
         */
        const handleCanPlay = () => {
            videoElement.current.playbackRate = playbackRate
            if (skipVideo.current) {
                videoElement.current.currentTime = Number(seekValue) % VIDEO_SKIP.CLIP_LENGTH
                skipVideo.current = false
            }
            videoElement?.current?.play()
            setCanPlayVideoLoading(false)
            setIsPlaying(true)
        }
        videoElement?.current?.addEventListener("canplay", handleCanPlay)

        return () => videoElement?.current?.removeEventListener("canplay", handleCanPlay)
    }, [currentVideoIndex])

    useEffect(() => {
        if (videoElement?.current) {
            videoElement.current.playbackRate = playbackRate
        }
    }, [playbackRate])

    useEffect(() => {
        if (seekValue === sessionVideoUrls.length * VIDEO_SKIP.CLIP_LENGTH && seekValue !== 0) {
            setIsSessionVideoEnded(true)
            setCanPlayVideoLoading(false)
        } else {
            setIsSessionVideoEnded(false)
        }
    }, [seekValue])

    const handleVideoEnd = () => {
        if (currentVideoIndex === sessionVideoUrls.length - 1) {
            setIsPlaying(false)
        } else {
            setCanPlayVideoLoading(true)
            setCurrentVideoIndex((prevIndex) => prevIndex + 1)
        }
    }

    const handleVideoProgress = () => {
        if (videoElement?.current?.currentTime) {
            const seekVal = (currentVideoIndex * VIDEO_SKIP.CLIP_LENGTH) + Math.floor(videoElement?.current?.currentTime)
            setSeekValue(seekVal)
        }
    }

    const playAndPauseVideo = () => {
        if (!videoElement.current) return

        if (videoElement.current.paused) {
            videoElement.current.playbackRate = playbackRate
            videoElement.current.play()
            setIsPlaying(true)
        } else {
            videoElement.current.pause()
            setIsPlaying(false)
        }
    }

    const handleSeekChange = (value) => {
        const newVideoIndex = Math.floor(value / VIDEO_SKIP.CLIP_LENGTH)
        const seekVal = newVideoIndex * VIDEO_SKIP.CLIP_LENGTH
        setCurrentVideoIndex(newVideoIndex)
        setCanPlayVideoLoading(true)
        setSeekValue(seekVal)
    }

    const handleAfterSeekChange = (value) => {
        const newVideoIndex = Math.floor(value / VIDEO_SKIP.CLIP_LENGTH)
        const currentTime = Math.floor(value % VIDEO_SKIP.CLIP_LENGTH)
        setCurrentVideoIndex(newVideoIndex)
    }

    const handleReplaySessionVideo = () => {
        setCurrentVideoIndex(0)
        setIsPlaying(true)
    }

    /**
     * Set the @param {skipVideo} boolean to true.
     * Increment current video index in case of forward otherwise decrement.
     * After this the control flow goes to useEffect with currentVideoIndex dependency.
     */
    const handleVideoSkip = (skipAction) => {
        skipVideo.current = true
        switch (skipAction) {
            case VIDEO_SKIP.ACTIONS.BACKWARD:
                setCurrentVideoIndex(prevIndex => {
                    const newIndex = prevIndex - (VIDEO_SKIP.SKIP_VAL / VIDEO_SKIP.CLIP_LENGTH)
                    if (newIndex < 0) return 0
                    return newIndex
                })
                break
            case VIDEO_SKIP.ACTIONS.FORWARD:
                setCurrentVideoIndex(prevIndex => {
                    const newIndex = prevIndex + (VIDEO_SKIP.SKIP_VAL / VIDEO_SKIP.CLIP_LENGTH)
                    if (newIndex > sessionVideoUrls.length) return sessionVideoUrls.length
                    return newIndex
                })
                break
            default: return
        }
    }

    return (
        <>
            <div className="video-container relative mb-10">
                {
                    loading
                        ? <div className="absolute top-0 left-0 loader-container flex items-center justify-center b-12">
                            <LoadingOutlined />
                        </div>
                        : sessionVideoUrls.length === 0
                            ? <div className="absolute top-0 left-0 loader-container flex items-center justify-center b-12">
                                <p className="text-18 weight-600 clr-gray">No video found</p>
                            </div>
                            :
                            <div>
                                {
                                    canPlayVideoLoading
                                        ? <div className="absolute top-0 left-0 loader-container loader-overlay flex items-center justify-center b-12">
                                            <LoadingOutlined />
                                        </div> : <></>
                                }
                                <video
                                    ref={videoElement}
                                    src={sessionVideoUrls[currentVideoIndex]?.link}
                                    onEnded={handleVideoEnd}
                                    onTimeUpdate={handleVideoProgress}
                                    className="video-player top-0 left-0 b-12"
                                />
                            </div>
                }
            </div>
            {
                !loading
                    ? <>
                        <Slider
                            disabled={sessionVideoUrls?.length === 0}
                            tooltip={{
                                formatter: getFormattedSeekValue,
                            }}
                            min={0}
                            max={sessionVideoUrls?.length * VIDEO_SKIP.CLIP_LENGTH}
                            value={seekValue}
                            onChange={handleSeekChange}
                            onAfterChange={handleAfterSeekChange}
                        />
                        {
                            vadMarkers.length > 0
                                ? <div className="vad-markers-container">
                                    <div className="vad-markers-track">
                                        {vadMarkers.map((marker, index) =>
                                            <Tooltip
                                                key={index}
                                                title={`Speech detected · ${getFormattedSeekValue(marker.startSec)} · ${marker.durSec.toFixed(1)}s${marker.questionIndex ? ` · Q${marker.questionIndex}` : ""}${marker.flagged ? " · non-speech question" : ""}`}
                                            >
                                                <span
                                                    className={`vad-marker${marker.flagged ? " vad-marker-flagged" : ""}`}
                                                    style={{ left: `${marker.leftPct}%`, width: `${marker.widthPct}%` }}
                                                    onClick={() => seekToMarker(marker.startSec)}
                                                />
                                            </Tooltip>
                                        )}
                                    </div>
                                    <p className="vad-markers-legend text-12 flex items-center">
                                        <span className="vad-legend-swatch" />
                                        Speech detected
                                        {vadMarkers.some(m => m.flagged) && (
                                            <>
                                                <span className="vad-legend-swatch vad-legend-swatch-flagged" />
                                                Speech on a non-speech question
                                            </>
                                        )}
                                    </p>
                                </div>
                                : <></>
                        }
                        <div className={`controls-container w-80 flex items-center justify-between ${sessionVideoUrls.length === 0 || canPlayVideoLoading ? "disabled opacity-6" : ""}`}>
                            <div className="flex items-center">
                                <button
                                    className={`default-btn flex items-center`}
                                    onClick={isSessionVideoEnded ? handleReplaySessionVideo : playAndPauseVideo}
                                >
                                    {
                                        isSessionVideoEnded
                                            ? <UndoOutlined />
                                            : !isPlaying ? <PlayCircleFilled /> : <PauseCircleFilled />
                                    }
                                </button>
                                <span
                                    onClick={() => handleVideoSkip(VIDEO_SKIP.ACTIONS.BACKWARD)}
                                    className={`skip-btn cursor-pointer ml-14 ${seekValue < VIDEO_SKIP.SKIP_VAL ? "disabled opacity-6" : ""}`}
                                >
                                    <FastBackwardOutlined />
                                </span>
                                <p className="text-14 ml-10 mr-10">
                                    {getFormattedSeekValue(seekValue)}
                                </p>
                                <span
                                    onClick={() => handleVideoSkip(VIDEO_SKIP.ACTIONS.FORWARD)}
                                    className={`skip-btn cursor-pointer ${seekValue >= ((sessionVideoUrls?.length - (VIDEO_SKIP.SKIP_VAL / VIDEO_SKIP.CLIP_LENGTH)) * VIDEO_SKIP.CLIP_LENGTH)
                                        ? "disabled opacity-6" : ""}`}
                                >
                                    <FastForwardOutlined />
                                </span>
                            </div>
                            <Segmented
                                options={[
                                    {
                                        label: "1x",
                                        value: 1
                                    },
                                    {
                                        label: "1.5x",
                                        value: 1.5
                                    },
                                    {
                                        label: "2x",
                                        value: 2
                                    },
                                ]}
                                value={playbackRate}
                                onChange={setPlaybackRate}
                            />
                        </div>
                    </>
                    : <></>
            }
        </>
    )
}

export default VideoPlayer

